import { useCallback, useMemo, useRef, useState } from 'react';

import { ListPagination, Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { ModalRefProps } from 'src/common/components/Modal';
import { Table } from 'src/common/components/Table';
import { OrbitIcons } from 'src/common/constants.';
import { useQuery } from 'src/common/hooks/useQuery';
import { scalingToCampaign } from 'src/tcr/api';
import { AccountProps } from 'src/tcr/types';

import { ScalingModal } from './ScalingModal';

interface AccountListProps {
  query?: string;
}

export const AccountList = ({ query = '' }: AccountListProps): JSX.Element => {
  const [currentPage, setCurrentPage] = useState(1);
  const scalingModalRef = useRef<ModalRefProps | null>(null);
  const { data, isLoading } = useQuery(
    `/account_admin?parent_blank=true&page=${currentPage}&search=${query}`
  );
  const accounts = (data as { results: Array<AccountProps> })?.results || [];
  const totalCount = data?.count;

  const handleScaling = useCallback((row, brand_id) => {
    scalingModalRef.current?.show(row, brand_id);
  }, []);

  const columns = useMemo(
    () => [
      { title: 'Name', index: 'name' },
      {
        title: 'TCR Ready',
        index: 'tcr_ready',
        renderer: (row: AccountProps) =>
          row.tcr_ready ? (
            <Icon name={OrbitIcons.Check} className="text-secondary-0.0" />
          ) : (
            <Icon name={OrbitIcons.X} className="text-red-1.0" />
          ),
      },
      {
        title: 'Brand',
        index: 'brand',
        renderer: (row: AccountProps) => {
          if (row.brands)
            return row.brands.map((brand) => (
              <div className="flex items-center py-2" key={brand.brand_id}>
                {brand.brand_id}
                {brand.brand_id === row.tcr_brand?.brand_id && (
                  <button
                    className="ml-auto btn bg-white-2.0"
                    onClick={() => handleScaling(row, brand.id)}
                  >
                    <Icon name={OrbitIcons.Scaling} />
                  </button>
                )}
              </div>
            ));
          return '';
        },
      },
    ],
    [handleScaling]
  );

  const handleSave = async (
    parent_account: AccountProps,
    brand_id: string,
    use_case: string
  ): Promise<void> => {
    try {
      await scalingToCampaign({ account_ids: [parent_account.id], use_case, brand_id });
      Toast.success('Account has been scaling successfully.');
    } catch (error) {
      Toast.error('Error occurred while scaling account.');
    }
  };

  return (
    <>
      <div className="scrollbar-thin flex-1 overflow-auto">
        <Table<AccountProps> data={accounts} columns={columns} loading={isLoading} />
      </div>
      {totalCount > 20 && (
        <ListPagination
          currentPage={currentPage}
          totalCount={totalCount}
          setCurrentPage={setCurrentPage}
        />
      )}
      <ScalingModal ref={scalingModalRef} onSave={handleSave} />
    </>
  );
};
